import { useCallback, useEffect, useRef } from 'react';

import { logWalletChanges } from '@/entities/user/helpers';
import { useUserActions } from '@/entities/user/model/user.store';
import { usePauseWalletWS, useWalletActions } from '@/entities/user/model/wallet.store';
import { type Wallet } from '@/entities/user/model/wallet.types';

import { usePrevious } from '@/shared/hooks/use-previous';
import { useRefValue } from '@/shared/hooks/use-ref-value';
import { useWs } from '@/shared/hooks/use-ws';
import { MessageType } from '@/shared/hooks/use-ws/types';
import { logErrorMini } from '@/shared/services/service-logger/lib/log-error-mini';
import { useAppState } from '@/shared/store/app-store/app-store';

export const useWalletSubscription = () => {
  const ws = useWs();

  const { wsSentInitialMsg } = useAppState();
  const pauseWalletWS = usePauseWalletWS();

  const { fetchUser } = useUserActions();
  const { hydrate } = useWalletActions();

  const subscriptionIdRef = useRef<string | null>(null);

  const pauseWalletWSRef = useRefValue(pauseWalletWS);
  const prevPauseWalletWS = usePrevious(pauseWalletWS);

  const handleWalletWS = useCallback(
    ({ payload: { wallets = [] } }: { payload: { wallets: Wallet[] } }) => {
      const pausedStoreUpdates = pauseWalletWSRef.current;

      logWalletChanges(wallets, pausedStoreUpdates);

      if (pausedStoreUpdates) return;

      hydrate((prev) => {
        const prevWallets = prev?.wallets ?? [];

        const newWallets = prevWallets.map((prevWallet) => {
          const newItem = wallets.find((newItem) => newItem.type === prevWallet.type);
          return newItem ? newItem : prevWallet;
        });

        return {
          wallets: prevWallets.length ? newWallets : undefined,
        };
      });
    },
    [hydrate, pauseWalletWSRef]
  );

  const subscribeWsWithErrorHandling = useCallback(() => {
    try {
      subscriptionIdRef.current = ws.subscribe(MessageType.WALLET, handleWalletWS);
    } catch (err) {
      void logErrorMini(err as Error, 'ws subscription err');
    }
  }, [handleWalletWS, ws]);

  const unsubscribeWsWithErrorHandling = useCallback(() => {
    try {
      ws.unsubscribe(MessageType.WALLET, subscriptionIdRef!.current!);
    } catch (err) {
      void logErrorMini(err as Error, 'ws unsubscribe err');
    }
  }, [ws]);

  // Refetch a user when the wallet pause is turned off
  useEffect(() => {
    if (!pauseWalletWS && prevPauseWalletWS) {
      void fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pauseWalletWS]);

  useEffect(() => {
    if (!wsSentInitialMsg) return;

    if (!pauseWalletWS) {
      subscribeWsWithErrorHandling();
    } else {
      if (subscriptionIdRef.current) {
        unsubscribeWsWithErrorHandling();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pauseWalletWS, wsSentInitialMsg]);

  useEffect(() => {
    return () => {
      if (subscriptionIdRef.current) {
        ws.unsubscribe(MessageType.WALLET, subscriptionIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
